import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, FolderOpen, Plus, ChevronRight, ArrowLeft,
  Navigation, DollarSign, ShieldAlert, FileText, Shield,
  Calculator, Clock, Sparkles, Target, AlertTriangle,
  CheckCircle2, Trash2, BarChart3, Zap, Link2, Search, Database, Download
} from "lucide-react";
import { API } from "@/constants";
import SmartSelect, { CASE_TYPE_OPTIONS, JURISDICTION_OPTIONS, INJURY_TYPE_OPTIONS } from "@/components/shared/SmartSelect";

const STAGE_META = {
  intake: { label: "Intake", color: "bg-blue-500", idx: 0 },
  investigation: { label: "Investigation", color: "bg-indigo-500", idx: 1 },
  discovery: { label: "Discovery", color: "bg-purple-500", idx: 2 },
  motions: { label: "Motions", color: "bg-amber-500", idx: 3 },
  mediation: { label: "Mediation", color: "bg-teal-500", idx: 4 },
  trial_prep: { label: "Trial Prep", color: "bg-orange-500", idx: 5 },
  trial: { label: "Trial", color: "bg-red-500", idx: 6 },
  settlement: { label: "Settlement", color: "bg-emerald-500", idx: 7 },
  closed: { label: "Closed", color: "bg-slate-500", idx: 8 },
};
const STAGES = Object.keys(STAGE_META);

const TOOL_LINKS = [
  { id: "litigation-gps", label: "Litigation GPS", icon: Navigation, color: "from-slate-800 to-blue-900", desc: "Judge, counsel & carrier intel" },
  { id: "live-investigator", label: "Live Investigator", icon: Shield, color: "from-blue-700 to-blue-900", desc: "Real-time API-powered investigations" },
  { id: "insurance-investigation", label: "Insurance Report", icon: FileText, color: "from-orange-600 to-amber-700", desc: "CLUE-style claims analysis" },
  { id: "expert-witness", label: "Expert Witness", icon: Target, color: "from-purple-700 to-indigo-800", desc: "Expert marketplace & engagement" },
  { id: "financial-engine", label: "Financial Engine", icon: DollarSign, color: "from-emerald-600 to-green-700", desc: "Valuation & cost forecast" },
  { id: "damages-calculator", label: "Damages Calculator", icon: Calculator, color: "from-orange-600 to-red-700", desc: "Economic & punitive damages" },
  { id: "fraud-analytics", label: "Fraud Analytics", icon: ShieldAlert, color: "from-red-700 to-red-900", desc: "Benford's law & patterns" },
  { id: "court-forms", label: "Court Forms", icon: FileText, color: "from-blue-700 to-indigo-800", desc: "Browse & auto-fill forms" },
  { id: "triple-shield", label: "Triple Shield", icon: Shield, color: "from-slate-800 to-blue-900", desc: "SelleShield + AML" },
];

const fmt = (n) => typeof n === "number" && n > 0 ? `$${n.toLocaleString()}` : "—";

// ─── Case List ─────────────────────────────────────────────
const CaseList = ({ onSelect, onCreate }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    const q = filter === "all" ? "" : `?stage=${filter}`;
    fetch(`${API}/case-workspace/cases${q}`)
      .then(r => r.json()).then(j => setCases(j.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Active Cases</h2>
          <p className="text-xs text-slate-500">{cases.length} cases</p>
        </div>
        <Button onClick={onCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-case-btn">
          <Plus className="h-4 w-4 mr-1.5" />New Case
        </Button>
      </div>
      {/* Stage Filters */}
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setFilter("all")} className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${filter === "all" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>All</button>
        {STAGES.map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${filter === s ? `${STAGE_META[s].color} text-white` : "bg-slate-100 text-slate-600"}`}>
            {STAGE_META[s].label}
          </button>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {cases.map(c => {
            const stage = STAGE_META[c.stage] || STAGE_META.intake;
            const progress = ((stage.idx + 1) / STAGES.length) * 100;
            return (
              <button key={c.id} onClick={() => onSelect(c.id)} className="w-full text-left p-4 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all" data-testid={`case-${c.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-800 truncate">{c.case_title}</span>
                      <Badge className={`${stage.color} text-white text-[9px] shrink-0`}>{stage.label}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{c.case_type} {c.jurisdiction && `| ${c.jurisdiction}`} {c.case_number && `| #${c.case_number}`}</p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-sm font-bold text-slate-700">{fmt(c.estimated_value)}</p>
                    <p className="text-[10px] text-slate-400">{Object.keys(c.tool_results || {}).length} tools used</p>
                  </div>
                </div>
                <Progress value={progress} className="h-1" />
              </button>
            );
          })}
          {cases.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No cases yet. Create your first case to get started.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Create Case Form ──────────────────────────────────────
const CreateCase = ({ onBack, onCreated }) => {
  const [form, setForm] = useState({ case_title: "", case_type: "", jurisdiction: "", plaintiff_name: "", defendant_name: "", plaintiff_attorney: "", defendant_attorney: "", court_name: "", case_number: "", judge_name: "", filing_date: "", description: "", injury_type: "", estimated_value: 0 });
  const [loading, setLoading] = useState(false);
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.case_title) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/case-workspace/cases`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, estimated_value: Number(form.estimated_value) }) });
      const j = await r.json();
      if (j.data?.id) onCreated(j.data.id);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back</button>
      <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #1E293B, #334155)" }}>
        <h2 className="text-lg font-bold text-white">Create New Case</h2>
        <p className="text-sm text-slate-300">All tools will link to this case for unified analysis</p>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label className="text-xs font-semibold">Case Title *</Label><Input value={form.case_title} onChange={e => u("case_title", e.target.value)} placeholder="Smith v. Acme Corp" className="mt-1" data-testid="new-case-title" /></div>
            <div><Label className="text-xs font-semibold">Case Type</Label><div className="mt-1"><SmartSelect value={form.case_type} onValueChange={v => u("case_type", v)} options={CASE_TYPE_OPTIONS} placeholder="Select..." /></div></div>
            <div><Label className="text-xs font-semibold">Jurisdiction</Label><div className="mt-1"><SmartSelect value={form.jurisdiction} onValueChange={v => u("jurisdiction", v)} options={JURISDICTION_OPTIONS} placeholder="Select..." /></div></div>
            <div><Label className="text-xs font-semibold">Plaintiff</Label><Input value={form.plaintiff_name} onChange={e => u("plaintiff_name", e.target.value)} placeholder="Plaintiff name" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Defendant</Label><Input value={form.defendant_name} onChange={e => u("defendant_name", e.target.value)} placeholder="Defendant name" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Court</Label><Input value={form.court_name} onChange={e => u("court_name", e.target.value)} placeholder="Court name" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Case Number</Label><Input value={form.case_number} onChange={e => u("case_number", e.target.value)} placeholder="2025-CV-1234" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Judge</Label><Input value={form.judge_name} onChange={e => u("judge_name", e.target.value)} placeholder="Judge name" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Filing Date</Label><Input type="date" value={form.filing_date} onChange={e => u("filing_date", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Injury Type</Label><div className="mt-1"><SmartSelect value={form.injury_type} onValueChange={v => u("injury_type", v)} options={INJURY_TYPE_OPTIONS} placeholder="Select..." /></div></div>
            <div><Label className="text-xs font-semibold">Estimated Value ($)</Label><Input type="number" value={form.estimated_value} onChange={e => u("estimated_value", e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs font-semibold">Description</Label><Textarea value={form.description} onChange={e => u("description", e.target.value)} placeholder="Brief case facts..." className="mt-1 min-h-[80px]" /></div>
        </CardContent>
      </Card>
      <Button onClick={submit} disabled={loading || !form.case_title} className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base" data-testid="submit-case-btn">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="h-5 w-5 mr-2" />Create Case</>}
      </Button>
    </div>
  );
};

// ─── Case Detail View ──────────────────────────────────────
const CaseDetail = ({ caseId, onBack }) => {
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [linkedItems, setLinkedItems] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);

  const loadCase = () => {
    setLoading(true);
    fetch(`${API}/case-workspace/cases/${caseId}`)
      .then(r => r.json()).then(j => setCaseData(j.data))
      .catch(() => {}).finally(() => setLoading(false));
  };

  const loadLinked = () => {
    setLinkedLoading(true);
    fetch(`${API}/case-workspace/cases/${caseId}/linked-items`)
      .then(r => r.json()).then(j => setLinkedItems(j.data || []))
      .catch(() => {}).finally(() => setLinkedLoading(false));
  };

  useEffect(() => { loadCase(); loadLinked(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStage = async (stage) => {
    await fetch(`${API}/case-workspace/cases/${caseId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ stage }) });
    loadCase();
  };

  const genSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await fetch(`${API}/case-workspace/cases/${caseId}/ai-summary`, { method: "POST" });
      const j = await r.json();
      setAiSummary(j.data?.ai_summary);
    } catch (e) { console.error(e); } finally { setSummaryLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!caseData) return <p className="text-center text-slate-500">Case not found.</p>;

  const c = caseData;
  const stage = STAGE_META[c.stage] || STAGE_META.intake;
  const progress = ((stage.idx + 1) / STAGES.length) * 100;
  const timeline = (c.timeline || []).slice().reverse();
  const toolResults = c.tool_results || {};

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-cases"><ArrowLeft className="h-4 w-4" />All Cases</button>
        <Button variant="outline" size="sm" className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
          data-testid="export-pdf-btn"
          onClick={() => { window.open(`${API}/case-workspace/cases/${caseId}/export-pdf`, '_blank'); }}>
          <Download className="h-3.5 w-3.5" />Export to Court (PDF)
        </Button>
      </div>

      {/* Case Header */}
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #1E293B, #0F172A)" }}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-black">{c.case_title}</h2>
            <p className="text-sm text-slate-300">{c.case_type} {c.jurisdiction && `| ${c.jurisdiction}`} {c.case_number && `| #${c.case_number}`}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black">{fmt(c.estimated_value)}</p>
            <Badge className={`${stage.color} text-white`}>{stage.label}</Badge>
          </div>
        </div>
        {/* Stage Pipeline */}
        <div className="mt-4">
          <div className="flex gap-1">
            {STAGES.map((s, i) => {
              const meta = STAGE_META[s];
              const active = i <= stage.idx;
              return (
                <button key={s} onClick={() => updateStage(s)} className={`flex-1 h-2 rounded-full transition-all ${active ? meta.color : "bg-white/20"}`} title={meta.label} data-testid={`stage-${s}`} />
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[9px] text-slate-400">
            <span>Intake</span><span>Discovery</span><span>Trial</span><span>Closed</span>
          </div>
        </div>
      </div>

      <Tabs defaultValue="tools" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 h-auto p-1">
          <TabsTrigger value="tools" className="text-xs py-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Zap className="h-3.5 w-3.5 mr-1" />Tools
          </TabsTrigger>
          <TabsTrigger value="linked" className="text-xs py-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-linked-items">
            <Link2 className="h-3.5 w-3.5 mr-1" />Linked ({linkedItems.length})
          </TabsTrigger>
          <TabsTrigger value="timeline" className="text-xs py-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Clock className="h-3.5 w-3.5 mr-1" />Timeline ({timeline.length})
          </TabsTrigger>
          <TabsTrigger value="summary" className="text-xs py-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Sparkles className="h-3.5 w-3.5 mr-1" />AI Summary
          </TabsTrigger>
        </TabsList>

        {/* Tools Tab */}
        <TabsContent value="tools">
          <div className="space-y-4">
            {/* Case Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {c.plaintiff_name && <div className="p-3 rounded-xl bg-blue-50 text-center"><p className="text-[10px] text-slate-500 uppercase">Plaintiff</p><p className="text-sm font-bold text-slate-800">{c.plaintiff_name}</p></div>}
              {c.defendant_name && <div className="p-3 rounded-xl bg-red-50 text-center"><p className="text-[10px] text-slate-500 uppercase">Defendant</p><p className="text-sm font-bold text-slate-800">{c.defendant_name}</p></div>}
              {c.judge_name && <div className="p-3 rounded-xl bg-purple-50 text-center"><p className="text-[10px] text-slate-500 uppercase">Judge</p><p className="text-sm font-bold text-slate-800">{c.judge_name}</p></div>}
              {c.court_name && <div className="p-3 rounded-xl bg-slate-50 text-center"><p className="text-[10px] text-slate-500 uppercase">Court</p><p className="text-sm font-bold text-slate-800">{c.court_name}</p></div>}
            </div>
            {/* Tool Launch Cards */}
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Launch Tools for This Case</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {TOOL_LINKS.map(tool => {
                const hasResult = !!toolResults[tool.id];
                return (
                  <button key={tool.id} onClick={() => navigate(`/tools/${tool.id}`)}
                    className={`text-left p-4 rounded-2xl text-white bg-gradient-to-r ${tool.color} hover:opacity-90 transition-all relative`}
                    data-testid={`launch-${tool.id}`}>
                    {hasResult && <CheckCircle2 className="h-4 w-4 absolute top-3 right-3 text-green-300" />}
                    <div className="flex items-center gap-3">
                      <tool.icon className="h-6 w-6 opacity-80" />
                      <div>
                        <p className="text-sm font-bold">{tool.label}</p>
                        <p className="text-xs opacity-80">{tool.desc}</p>
                        {hasResult && <p className="text-[10px] opacity-60 mt-0.5">Last run: {toolResults[tool.id].last_run?.slice(0, 10)}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Linked Items Tab */}
        <TabsContent value="linked">
          <div className="space-y-3">
            {linkedLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : linkedItems.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  <Link2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="font-medium">No linked items yet</p>
                  <p className="text-sm mt-1">Run investigations or generate reports, then use "Link to Case" to connect them here.</p>
                </CardContent>
              </Card>
            ) : linkedItems.map((item, i) => (
              <Card key={i} className="hover:shadow-md transition-shadow" data-testid={`linked-item-${i}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {item.item_type === "investigation" ? <Search className="w-4 h-4 text-blue-600" /> :
                         item.item_type === "insurance_report" ? <FileText className="w-4 h-4 text-orange-600" /> :
                         <Target className="w-4 h-4 text-purple-600" />}
                        <span className="font-semibold text-sm">{item.title}</span>
                        <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-600">{item.item_type.replace("_", " ")}</span>
                      </div>
                      {item.summary && <p className="text-xs text-slate-500 mb-2">{item.summary}</p>}
                      {item.detail && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          {item.detail.risk_level && (
                            <div><span className="text-slate-400">Risk:</span> <span className={`font-bold ${item.detail.risk_level === "CRITICAL" || item.detail.risk_level === "HIGH" ? "text-red-600" : "text-green-600"}`}>{item.detail.risk_level} ({item.detail.risk_score}/100)</span></div>
                          )}
                          {item.detail.sources_used && <div><span className="text-slate-400">APIs:</span> {item.detail.sources_used.length} sources</div>}
                          {item.detail.findings_count !== undefined && <div><span className="text-slate-400">Findings:</span> {item.detail.findings_count}</div>}
                          {item.detail.claim_type && <div><span className="text-slate-400">Claim:</span> {item.detail.claim_type}</div>}
                          {item.detail.claimed_amount && <div><span className="text-slate-400">Amount:</span> ${item.detail.claimed_amount.toLocaleString()}</div>}
                          {item.detail.fraud_indicators?.length > 0 && (
                            <div className="col-span-2 md:col-span-4">
                              <span className="text-slate-400">Fraud Flags:</span>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {item.detail.fraud_indicators.map((f, j) => <span key={j} className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-xs">{f}</span>)}
                              </div>
                            </div>
                          )}
                          {item.detail.data_sources?.length > 0 && (
                            <div className="col-span-2 md:col-span-4">
                              <span className="text-slate-400">Verified via:</span>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {item.detail.data_sources.map((s, j) => <span key={j} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{s}</span>)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-xs text-slate-400 shrink-0">
                      {new Date(item.linked_at).toLocaleDateString()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <div className="space-y-3">
            {timeline.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No activity yet.</p>
            ) : timeline.map((entry, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full shrink-0 ${i === 0 ? "bg-blue-500" : "bg-slate-300"}`} />
                  {i < timeline.length - 1 && <div className="w-px flex-1 bg-slate-200" />}
                </div>
                <div className="pb-4 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className="text-[9px]">{entry.tool}</Badge>
                    <span className="text-xs text-slate-400">{entry.timestamp?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800">{entry.action}</p>
                  <p className="text-xs text-slate-500">{entry.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* AI Summary Tab */}
        <TabsContent value="summary">
          <div className="space-y-4">
            <Button onClick={genSummary} disabled={summaryLoading} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 h-11" data-testid="gen-summary-btn">
              {summaryLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate AI Case Summary</>}
            </Button>
            {aiSummary && (
              <div className="space-y-4 animate-in fade-in duration-500">
                {aiSummary.executive_summary && (
                  <Card className="border-0 shadow-sm bg-blue-50">
                    <CardContent className="pt-5">
                      <p className="text-sm font-semibold text-blue-800 mb-1">Executive Summary</p>
                      <p className="text-sm text-blue-700">{aiSummary.executive_summary}</p>
                    </CardContent>
                  </Card>
                )}
                {aiSummary.case_strength !== undefined && (
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                    <Target className="h-5 w-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Case Strength</p>
                      <Progress value={aiSummary.case_strength} className="h-2.5 mt-1" />
                    </div>
                    <span className="text-xl font-black text-slate-800">{aiSummary.case_strength}/100</span>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {aiSummary.key_findings && (
                    <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
                      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-600" />Key Findings</CardTitle></CardHeader>
                      <CardContent><div className="space-y-1.5">{aiSummary.key_findings.map((f, i) => <div key={i} className="flex items-start gap-2 text-sm"><ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" /><span className="text-slate-700">{f}</span></div>)}</div></CardContent>
                    </Card>
                  )}
                  {aiSummary.recommended_next_steps && (
                    <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
                      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-emerald-600" />Next Steps</CardTitle></CardHeader>
                      <CardContent><div className="space-y-1.5">{aiSummary.recommended_next_steps.map((s, i) => <div key={i} className="flex items-start gap-2 text-sm"><ChevronRight className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /><span className="text-slate-700">{s}</span></div>)}</div></CardContent>
                    </Card>
                  )}
                </div>
                {aiSummary.risk_assessment && <Card className="border-0 shadow-sm bg-amber-50"><CardContent className="pt-5"><p className="text-sm"><AlertTriangle className="h-4 w-4 inline text-amber-600 mr-1" /><span className="font-semibold text-amber-800">Risk: </span><span className="text-amber-700">{aiSummary.risk_assessment}</span></p></CardContent></Card>}
                {aiSummary.settlement_recommendation && <Card className="border-0 shadow-sm bg-emerald-50"><CardContent className="pt-5"><p className="text-sm"><DollarSign className="h-4 w-4 inline text-emerald-600 mr-1" /><span className="font-semibold text-emerald-800">Settlement: </span><span className="text-emerald-700">{aiSummary.settlement_recommendation}</span></p></CardContent></Card>}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────
const CaseWorkspacePage = () => {
  const [view, setView] = useState("list"); // list | create | detail
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-6" data-testid="case-workspace">
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #0F172A, #1E3A5F)" }}>
        <div className="flex items-center gap-3 mb-1">
          <FolderOpen className="h-7 w-7 text-blue-400" />
          <h1 className="text-2xl font-black tracking-tight">Case Workspace</h1>
          <Badge className="bg-blue-500/20 text-blue-200 border-blue-500/30">UNIFIED</Badge>
        </div>
        <p className="text-blue-200 text-sm">Central hub — all tools linked to a single matter with timeline, status tracking, and AI summaries</p>
      </div>

      {view === "list" && <CaseList onSelect={id => { setSelectedCaseId(id); setView("detail"); }} onCreate={() => setView("create")} />}
      {view === "create" && <CreateCase onBack={() => setView("list")} onCreated={id => { setSelectedCaseId(id); setView("detail"); }} />}
      {view === "detail" && selectedCaseId && <CaseDetail caseId={selectedCaseId} onBack={() => setView("list")} />}
    </div>
  );
};

export default CaseWorkspacePage;
